/**
 * useAnalytics Hook
 *
 * Automatically tracks page views, time on page, and provides action tracking
 */

import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { analyticsAPI } from '../services/api'
import { getDeviceInfo, getSessionId, clearSessionId } from '../utils/deviceDetection'

// Event queue for batching
let eventQueue = []
let flushTimeout = null
const FLUSH_INTERVAL = 5000 // 5 seconds
const MAX_QUEUE_SIZE = 10

/**
 * Flush queued events to the server
 */
async function flushEvents() {
  if (eventQueue.length === 0) return

  const eventsToSend = [...eventQueue]
  eventQueue = []

  try {
    if (eventsToSend.length === 1) {
      await analyticsAPI.track(eventsToSend[0])
    } else {
      await analyticsAPI.trackBatch(eventsToSend)
    }
  } catch (error) {
    // Re-queue failed events (but limit to prevent infinite growth)
    if (eventQueue.length < MAX_QUEUE_SIZE * 2) {
      eventQueue = [...eventsToSend, ...eventQueue]
    }
    console.error('[Analytics] Failed to send events:', error)
  }
}

/**
 * Queue an event for sending
 */
function queueEvent(event) {
  eventQueue.push(event)

  // Flush if queue is full
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    flushEvents()
  } else {
    // Schedule flush if not already scheduled
    if (!flushTimeout) {
      flushTimeout = setTimeout(() => {
        flushTimeout = null
        flushEvents()
      }, FLUSH_INTERVAL)
    }
  }
}

/**
 * Main analytics hook
 * Call this once at the app level to enable automatic tracking
 */
export function useAnalytics() {
  const location = useLocation()
  const { isAuthenticated, user, currentClinicId } = useAuthStore()
  const pageStartTime = useRef(Date.now())
  const lastPath = useRef(null)
  const deviceInfo = useRef(null)

  // Get device info once on mount
  useEffect(() => {
    deviceInfo.current = getDeviceInfo()
  }, [])

  // Track page view on route change
  useEffect(() => {
    if (!isAuthenticated || !user) return

    const currentPath = location.pathname

    // Send duration for previous page if exists
    if (lastPath.current && lastPath.current !== currentPath) {
      const duration = Date.now() - pageStartTime.current
      sendPageViewEnd(lastPath.current, duration)
    }

    // Track new page view
    pageStartTime.current = Date.now()
    lastPath.current = currentPath

    trackPageView(currentPath)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAuthenticated, user?.id])

  // Track page duration on visibility change and unload
  useEffect(() => {
    if (!isAuthenticated || !user) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && lastPath.current) {
        const duration = Date.now() - pageStartTime.current
        sendPageViewEnd(lastPath.current, duration)
        // Reset timer for when user returns
        pageStartTime.current = Date.now()
      }
    }

    const handleBeforeUnload = () => {
      if (lastPath.current) {
        const duration = Date.now() - pageStartTime.current
        sendPageViewEndSync(lastPath.current, duration)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id])

  // Track page view
  const trackPageView = useCallback((path) => {
    if (!isAuthenticated) return

    const event = {
      eventType: 'page_view',
      sessionId: getSessionId(),
      page: {
        path,
        title: document.title,
        referrer: lastPath.current || ''
      },
      device: deviceInfo.current,
      clinicId: currentClinicId
    }

    queueEvent(event)
  }, [isAuthenticated, currentClinicId])

  // Send page view with duration (for navigation/visibility change)
  const sendPageViewEnd = useCallback((path, duration) => {
    if (!isAuthenticated || duration < 1000) return // Ignore very short durations

    const event = {
      eventType: 'page_view',
      sessionId: getSessionId(),
      page: {
        path,
        title: document.title,
        duration: Math.min(duration, 30 * 60 * 1000) // Cap at 30 minutes
      },
      device: deviceInfo.current,
      clinicId: currentClinicId
    }

    queueEvent(event)
  }, [isAuthenticated, currentClinicId])

  // Sync version for beforeunload (uses sendBeacon)
  const sendPageViewEndSync = useCallback((path, duration) => {
    if (!isAuthenticated || duration < 1000) return

    const event = {
      eventType: 'page_view',
      sessionId: getSessionId(),
      page: {
        path,
        title: document.title,
        duration: Math.min(duration, 30 * 60 * 1000)
      },
      device: deviceInfo.current,
      clinicId: currentClinicId
    }

    // Use sendBeacon for reliable delivery on page unload
    const token = localStorage.getItem('auth-token')
    if (navigator.sendBeacon && token) {
      const blob = new Blob([JSON.stringify(event)], { type: 'application/json' })
      navigator.sendBeacon('/api/analytics/track', blob)
    }
  }, [isAuthenticated, currentClinicId])

  // Track custom action
  const trackAction = useCallback((actionType, target, data = {}) => {
    if (!isAuthenticated) return

    const event = {
      eventType: 'action',
      sessionId: getSessionId(),
      action: {
        type: actionType,
        target,
        data
      },
      page: {
        path: location.pathname
      },
      device: deviceInfo.current,
      clinicId: currentClinicId
    }

    queueEvent(event)
  }, [isAuthenticated, currentClinicId, location.pathname])

  // Track login event
  const trackLogin = useCallback(() => {
    const event = {
      eventType: 'login',
      sessionId: getSessionId(),
      device: deviceInfo.current,
      clinicId: currentClinicId
    }

    // Send immediately for login
    analyticsAPI.track(event).catch(console.error)
  }, [currentClinicId])

  // Track logout event
  const trackLogout = useCallback(() => {
    // Flush pending events
    flushEvents()

    const event = {
      eventType: 'logout',
      sessionId: getSessionId(),
      device: deviceInfo.current,
      clinicId: currentClinicId
    }

    // Send immediately using sendBeacon for reliability
    const token = localStorage.getItem('auth-token')
    if (navigator.sendBeacon && token) {
      const blob = new Blob([JSON.stringify(event)], { type: 'application/json' })
      navigator.sendBeacon('/api/analytics/track', blob)
    }

    // Clear session for next login
    clearSessionId()
  }, [currentClinicId])

  return {
    trackAction,
    trackLogin,
    trackLogout,
    trackPageView
  }
}

/**
 * Create analytics context for use throughout the app
 * This is a simpler interface for components that just need trackAction
 */
export function useTrackAction() {
  const { isAuthenticated, currentClinicId } = useAuthStore()
  const location = useLocation()
  const deviceInfo = useRef(null)

  useEffect(() => {
    deviceInfo.current = getDeviceInfo()
  }, [])

  const trackAction = useCallback((actionType, target, data = {}) => {
    if (!isAuthenticated) return

    const event = {
      eventType: 'action',
      sessionId: getSessionId(),
      action: {
        type: actionType,
        target,
        data
      },
      page: {
        path: location.pathname
      },
      device: deviceInfo.current,
      clinicId: currentClinicId
    }

    queueEvent(event)
  }, [isAuthenticated, currentClinicId, location.pathname])

  return trackAction
}

export default useAnalytics
